import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowUpRight, TrendingDown, TrendingUp,
  Calendar as CalendarIcon, Infinity as InfinityIcon,
} from "lucide-react";
import { api } from "@/lib/api";

function shortDay(iso) {
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "short" });
  } catch { return ""; }
}

/** Tiny SVG sparkline. No external chart lib needed. */
function Sparkline({ data, width = 280, height = 64 }) {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map((d) => d.spent));
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const pts = data.map((d, i) => [i * stepX, height - (d.spent / max) * (height - 8) - 4]);

  // Build smooth path
  const path = pts.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(" ");
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-16"
      data-testid="credits-sparkline"
    >
      <defs>
        <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF8A1F" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FF8A1F" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-grad)" />
      <path d={path} fill="none" stroke="#FF8A1F" strokeWidth="1.5" strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="#FF8A1F" opacity={data[i].spent > 0 ? 1 : 0.25} />
      ))}
    </svg>
  );
}

export default function CreditsBurnRateWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/credits/usage-trend?days=7");
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return <div className="rounded-xl border border-line bg-bg-surface p-6 font-mono text-xs text-ink-muted">LOADING BURN RATE...</div>;
  }
  if (!data) return null;

  // Admin → unlimited tile, skip chart heuristics
  if (data.admin_unlimited) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="burn-rate-widget">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-violet-300" />
            <h3 className="font-display text-lg font-bold">Credits burn rate</h3>
          </div>
          <Link to="/dashboard/billing?tab=credits" className="text-xs text-brand-cyan hover:underline inline-flex items-center gap-1">
            Billing <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="flex items-center gap-2 py-6">
          <InfinityIcon className="h-7 w-7 text-violet-300" />
          <span className="font-display text-2xl font-bold">Unlimited</span>
        </div>
      </div>
    );
  }

  const today = data.today_spent || 0;
  const avg = data.average_per_day || 0;
  const proj = data.projection;
  const trendDown = today < avg;

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="burn-rate-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-brand-amber" />
          <h3 className="font-display text-lg font-bold">Credits burn rate</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">7d</span>
        </div>
        <Link
          to="/dashboard/billing?tab=credits"
          className="text-xs text-brand-cyan hover:underline inline-flex items-center gap-1"
          data-testid="burn-rate-billing-link"
        >
          Billing <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Tile label="Today" value={today} accent="amber" testid="burn-today" />
        <Tile
          label="Daily avg"
          value={avg}
          accent="cyan"
          icon={trendDown ? TrendingDown : TrendingUp}
          testid="burn-avg"
        />
        <Tile
          label={proj ? "Burn-through" : "Forecast"}
          value={proj ? `${Math.round(proj.days_until_empty)}d` : "—"}
          accent={proj && proj.days_until_empty < 7 ? "rose" : "emerald"}
          testid="burn-projection"
          suffix={proj && proj.days_until_empty < 9999 ? "" : ""}
        />
      </div>

      <Sparkline data={data.daily} />

      <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-ink-muted">
        {data.daily.map((d, i) => (
          <span key={d.date} className={i === data.daily.length - 1 ? "text-brand-amber" : ""}>
            {shortDay(d.date)}
          </span>
        ))}
      </div>

      {proj && proj.days_until_empty < 7 && (
        <div
          className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-300"
          data-testid="burn-rate-warning"
        >
          <CalendarIcon className="inline h-3.5 w-3.5 mr-1" />
          At this pace you'll deplete credits in <strong>{Math.round(proj.days_until_empty)} days</strong>.
          Consider a top-up.
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent = "amber", icon: Icon, testid, suffix = "" }) {
  const toneCls = {
    amber: "text-brand-amber",
    cyan: "text-brand-cyan",
    rose: "text-rose-400",
    emerald: "text-emerald-400",
  }[accent];
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1">
        {Icon && <Icon className={`h-3 w-3 ${toneCls}`} />}
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</span>
      </div>
      <div className={`font-display text-2xl font-bold ${toneCls}`}>
        {value}{suffix}
      </div>
    </div>
  );
}
